import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Close, ChevronRight, ExpandMore } from '@mui/icons-material';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { DeleteOutline, FileDownloadOutlined } from '@mui/icons-material';
import Papa from 'papaparse';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Grid2,
} from '@mui/material';

import {
  gray10,
  gray40,
  gray80,
  gray100,
  orange50,
  primaryButtonBackgroundColor,
  red50,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
  white_overlay80,
} from '../../../constants/colors';
import {
  boldMd,
  boldXxl,
  dialogTitle as dialogTitleStyle,
  regularLg,
  regularMd,
  regularSm,
  sectionTitle,
  spHeaderTitle,
} from '../../../constants/location';
import { DICTIONARY_MAX_ROWS } from '../../../constants/enum';
import { dictionaryCsvUploadSchema } from '../../../validation/input';
import { type InputUploadType } from '../../../types/input';
import {
  DictionaryColumnMapping,
  DictionaryMappingType,
  DictionaryRow,
  DictionaryType,
} from '../../../types/dictionary';
import { createDictionary } from '../../../api/firebase/firestore/dictionary/create';
import { getDictionariesByCompanyId } from '../../../api/firebase/firestore/dictionary/read';
import { updateDictionary } from '../../../api/firebase/firestore/dictionary/update';
import { deleteDictionary } from '../../../api/firebase/firestore/dictionary/delete';
import { getDictionaryMappings } from '../../../api/firebase/firestore/dictionary/mapping/read';
import { createDictionaryMapping } from '../../../api/firebase/firestore/dictionary/mapping/create';
import { updateDictionaryMapping } from '../../../api/firebase/firestore/dictionary/mapping/update';
import { deleteDictionaryMapping } from '../../../api/firebase/firestore/dictionary/mapping/delete';
import { getMastersByPatternId } from '../../../api/firebase/firestore/master/readByPattern';
import { getDictionariesByPatternId } from '../../../api/firebase/firestore/dictionary/readByPattern';

import CustomLoading from '../../uiParts/form/CustomLoading';
import CustomSelectField from '../../uiParts/form/CustomSelectField';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import FileUploader from '../../uiParts/form/FileUploader';
import SideBar from '../../uiParts/menu/SideBar';
import DictionaryMappingFormModal from '../../uiParts/modal/DictionaryMappingFormModal';
import DeleteModal from '../../uiParts/modal/DeleteModal';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import MetaTitle from '../../utils/MetaTitle';
import { usePageLayout } from '../../../hooks/usePageLayout';
import { useCompanyId } from '../../../hooks/useCompanyId';
import { buildUploaderFields, getDisplayName } from '../../../utils/displayName';
import { getUserErrorMessage } from '../../../modules/error';
import { usePatternList } from '../../../hooks/usePatternList';
import AccountCircle from '../../assets/Account circle.svg';
import HiStoryIcon from '../../assets/hi-story-icon.png';
import moment from 'moment';
import Encoding from 'encoding-japanese';

const styles = {
  avatar: {
    height: '24px',
    width: '24px',
  },
  buttonSmall: {
    height: '36px',
    '& .MuiButton-icon': {
      marginRight: '4px',
    },
  },
  headerButtonWrapper: {
    '@media screen and (max-width:960px)': {
      boxSizing: 'border-box',
      position: 'fixed',
      bottom: 0,
      left: 0,
      backgroundColor: white_overlay80,
      padding: '16px 16px 24px',
      width: '100%',
      zIndex: 1,
    },
  },
  uploadButton: {
    ...primaryButtonBackgroundColor,
    height: '44px',
    padding: '16px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '& .MuiButton-icon': { marginRight: '4px' },
    '@media screen and (max-width:960px)': {
      width: '100%',
    },
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogCloseButton: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  dialogTitleText: {
    ...dialogTitleStyle,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  icon18: {
    height: '18px',
    marginLeft: '0px',
    width: '18px',
  },
  iconButton: {
    padding: 0,
  },
  table: {
    minWidth: '880px',
    overflowX: 'auto',
  },
  tableCellAvatar: {
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
    height: '48px',
    padding: '0 16px',
  },
  tableCellButton: {
    padding: '0 16px 0 0',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  tableDataText: {
    ...regularSm,
    color: gray100,
    padding: '0 16px',
  },
  tableHeadText: {
    ...regularSm,
    color: gray80,
    padding: '0 16px',
    whiteSpace: 'nowrap',
  },
  tableHeadRow: {
    height: '48px',
  },
  tableDataRow: {
    height: '48px',
  },
  tableSection: {
    backgroundColor: white,
    border: '1px solid' + gray40,
    borderRadius: '4px',
    boxSizing: 'border-box',
    margin: '0 32px',
    minHeight: '560px',
    padding: '16px 24px 56px',
    width: 'calc(100% - 64px)',
    '@media screen and (max-width:960px)': {
      border: 'none',
      borderRadius: 0,
      margin: 0,
      width: '100%',
    },
  },
  tableWrapper: {
    overflowX: 'auto',
    width: '100%',
    WebkitOverflowScrolling: 'touch',
  },
  emptyIcon: {
    width: '56px',
    height: '56px',
    margin: '80px 0 24px 0',
    '@media screen and (max-width: 600px)': {
      margin: '24px 0 24px 0',
    },
  },
  emptyTitle: {
    whiteSpace: 'pre-wrap',
    mb: 2,
    ...sectionTitle,
    '@media screen and (max-width: 600px)': {
      marginBottom: '12px',
    },
  },
  emptyDescription: {
    whiteSpace: 'pre-wrap',
    mb: 4,
    ...regularMd,
    '@media screen and (max-width: 600px)': {
      marginBottom: '20px',
    },
  },
  uploadButtonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  uploadCancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
  },
  uploadCancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  uploadFileChip: {
    margin: '0 8px 8px 0',
  },
  uploadSubmitButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  uploadSubmitButtonText: {
    ...boldMd,
    color: white,
  },
  FileNameEllipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '248px',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  CreatedByEllipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '96px',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  mappingRow: {
    backgroundColor: gray10,
  },
  mappingCell: {
    ...regularSm,
    color: gray100,
    padding: '8px 16px',
  },
  mappingFooterRow: {
    backgroundColor: gray10,
  },
  expandIconButton: {
    padding: '4px',
  },
};

interface PatternItem {
  id: string;
  label: string;
}

interface MappingsState {
  [dictionaryId: string]: DictionaryMappingType[];
}

const Dictionary = (): ReactElement => {
  // 初期表示で「0件→loading→リスト」のちらつきを防ぐため、初期値を true にする（#2866）。
  // companyId 確定後に fetchDictionaries が走り、完了で false になる。
  const [loading, setLoading] = useState<boolean>(true);
  const [errorText, setErrorText] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [newUploadModalOpen, setNewUploadModalOpen] = useState<boolean>(false);
  const [replaceModalOpen, setReplaceModalOpen] = useState<boolean>(false);
  const [mappingModalOpen, setMappingModalOpen] = useState<boolean>(false);
  const [mappingTargetDictionary, setMappingTargetDictionary] = useState<DictionaryType | null>(
    null,
  );
  const [editingMapping, setEditingMapping] = useState<DictionaryMappingType | null>(null);
  const [replaceTargetDictionary, setReplaceTargetDictionary] = useState<DictionaryType | null>(
    null,
  );
  const [deleteTargetDictionary, setDeleteTargetDictionary] = useState<DictionaryType | null>(null);
  const [deleteMappingTarget, setDeleteMappingTarget] = useState<{
    dictionary: DictionaryType;
    mapping: DictionaryMappingType;
  } | null>(null);
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const { user, company, isLoading } = useContext(AuthContext);
  const { companyId, isProxyMode, operatorId } = useCompanyId();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const [dictionaries, setDictionaries] = useState<DictionaryType[]>([]);
  const [mappingsByDictionary, setMappingsByDictionary] = useState<MappingsState>({});
  const [expandedDictionaryIds, setExpandedDictionaryIds] = useState<Set<string>>(new Set());
  const [fileError, setFileError] = useState<string | null>(null);
  // 差し替えモーダルで選択中 CSV のデータ行数（行数オーバー時に保存ボタンを無効化するため保持）
  const [replaceRowCount, setReplaceRowCount] = useState<number>(0);
  const { patterns } = usePatternList();

  // 新規アップロードモーダル内でのマッピング設定用状態（CSV + 1件目のマッピングを一括登録する）
  const [newCsvColumns, setNewCsvColumns] = useState<string[]>([]);
  const [newRows, setNewRows] = useState<DictionaryRow[]>([]);
  const [newPatternId, setNewPatternId] = useState<string>('');
  const [newSearchItemId, setNewSearchItemId] = useState<string>('');
  const [newSearchCsvColumn, setNewSearchCsvColumn] = useState<string>('');
  const [newColumnMappings, setNewColumnMappings] = useState<DictionaryColumnMapping[]>([]);

  // 排他制御用: 選択中パターンでマスタ照合に使われている search_item_id の集合
  // （同じOCR項目にマスタ照合と辞書照合を両方設定させないため、マスタ側で使用済みの項目を disabled にする）
  const [newMasterUsedItemIds, setNewMasterUsedItemIds] = useState<Set<string>>(new Set());
  // 排他制御用: 選択中パターンで既存の辞書が使っている search_item_id の集合
  // （同じOCR項目に複数の辞書を割り当てさせないため、使用済みの項目を disabled にする）
  const [newDictionaryUsedItemIds, setNewDictionaryUsedItemIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isLoading) return;
    // 未認証時の退避は useAuthRedirect が行う（#3203）。ここは企業設定による振り分けのみ扱う
    if (user === null) return;
    if (company !== null && !company.enable_dictionary) navigate('/home');
  }, [user, company, isLoading, navigate]);

  // 指定パターンでマスタ照合に使用済みの search_item_id 集合を取得する（失敗時は空Setにフォールバック）
  const fetchMasterUsedItemIds = useCallback(
    async (patternId: string): Promise<Set<string>> => {
      if (!companyId || !patternId) return new Set();
      try {
        const masters = await getMastersByPatternId(companyId, patternId);
        return new Set(masters.map(m => m.search_item_id).filter((id): id is string => !!id));
      } catch {
        return new Set();
      }
    },
    [companyId],
  );

  // 指定パターンで既存の辞書が使用済みの search_item_id 集合を取得する（失敗時は空Setにフォールバック）
  const fetchDictionaryUsedItemIds = useCallback(
    async (patternId: string): Promise<Set<string>> => {
      if (!companyId || !patternId) return new Set();
      try {
        const dictionaries = await getDictionariesByPatternId(companyId, patternId);
        return new Set(dictionaries.map(d => d.search_item_id).filter((id): id is string => !!id));
      } catch {
        return new Set();
      }
    },
    [companyId],
  );

  // 新規モーダル: パターン変更時に使用済み項目（マスタ・既存辞書）を再取得
  useEffect(() => {
    let active = true;
    void fetchMasterUsedItemIds(newPatternId).then(ids => {
      if (active) setNewMasterUsedItemIds(ids);
    });
    void fetchDictionaryUsedItemIds(newPatternId).then(ids => {
      if (active) setNewDictionaryUsedItemIds(ids);
    });
    return () => {
      active = false;
    };
  }, [fetchMasterUsedItemIds, fetchDictionaryUsedItemIds, newPatternId]);

  const fetchMappingsForDictionary = async (
    dictionaryId: string,
  ): Promise<DictionaryMappingType[]> => {
    const list = await getDictionaryMappings(dictionaryId);
    setMappingsByDictionary(prev => ({ ...prev, [dictionaryId]: list }));
    return list;
  };

  const fetchDictionaries = async (): Promise<void> => {
    if (!companyId) return;
    try {
      setLoading(true);
      const result = await getDictionariesByCompanyId(companyId);
      setDictionaries(result);
      await Promise.all(result.map(d => fetchMappingsForDictionary(d.dictionary_id)));
      setExpandedDictionaryIds(new Set(result.map(d => d.dictionary_id)));
    } catch {
      setErrorText('辞書データの取得に失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchDictionaries();
  }, [companyId]);

  const toggleExpand = (dictionaryId: string): void => {
    setExpandedDictionaryIds(prev => {
      const next = new Set(prev);
      if (next.has(dictionaryId)) next.delete(dictionaryId);
      else next.add(dictionaryId);
      return next;
    });
  };

  // ダブルクオート囲みのカンマを考慮してCSVの1行をフィールド配列に分解する
  const parseCsvLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        // 連続するダブルクオート("")はエスケープされた1つの"として扱う
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result.map(value => value.trim().replace(/^"|"$/g, ''));
  };

  // CSV ファイルを文字コード自動判定して読み込む（UTF-8 / Shift-JIS 両対応）。
  // file.text() は UTF-8 固定のため Shift-JIS が文字化けする。encoding-japanese で判定・変換する。
  const readCsvFileAsText = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const detected = Encoding.detect(bytes);
    const unicodeArray = Encoding.convert(bytes, {
      to: 'UNICODE',
      from: detected || 'AUTO',
    });
    return Encoding.codeToString(unicodeArray);
  };

  // CSVをパースしてヘッダー（csv_columns）と行データ（rows）を取得する
  const parseCsv = async (file: File): Promise<{ csvColumns: string[]; rows: DictionaryRow[] }> => {
    const text = await readCsvFileAsText(file);
    // Windows 改行(\r\n)・古い Mac 改行(\r) も考慮して行分割する
    const lines = text.split(/\r\n|\r|\n/).filter(line => line.trim());
    if (lines.length === 0) return { csvColumns: [], rows: [] };

    const csvColumns = parseCsvLine(lines[0]);
    const rows: DictionaryRow[] = lines.slice(1).map(line => {
      const values = parseCsvLine(line);
      const row: DictionaryRow = {};
      csvColumns.forEach((column, index) => {
        row[column] = values[index] ?? '';
      });
      return row;
    });

    return { csvColumns, rows };
  };

  const handleFilesDeleted = (): void => {
    setValue('images', []);
    trigger('images');
    setFileError(null);
    setReplaceRowCount(0);
  };

  const handleReplaceFilesSelected = async (
    files: FileList | null,
    onChange: (value: File[] | null) => void,
  ): Promise<void> => {
    setFileError(null);
    setReplaceRowCount(0);
    if (files && files.length > 0) {
      const selectedFile = files[0];
      // 拡張子チェック（.csv のみ許可）
      if (!/\.csv$/i.test(selectedFile.name)) {
        onChange(null);
        setFileError('CSV ファイル (.csv) を選択してください。');
        return;
      }
      onChange([selectedFile]);
      if (selectedFile.name !== replaceTargetDictionary?.file_name) {
        setFileError('選択したファイルの名前が一致しません。');
        return;
      }
      // 行数を判定し、上限超過なら保存ボタンを無効化する（新規登録モーダルと同じ挙動）
      const { rows } = await parseCsv(selectedFile);
      setReplaceRowCount(rows.length);
      if (rows.length === 0) {
        setFileError('データ行がありません。');
      } else if (rows.length > DICTIONARY_MAX_ROWS) {
        setFileError(`辞書は最大${DICTIONARY_MAX_ROWS}行までです。（現在: ${rows.length}行）`);
      }
    } else {
      onChange(null);
    }
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
  } = useForm<InputUploadType>({
    resolver: yupResolver(dictionaryCsvUploadSchema),
  });

  // パターン項目を親子展開して { id, label } の配列にする
  const buildPatternItems = (patternId: string): PatternItem[] => {
    const selectedPattern = patterns.find(p => p.patternId === patternId);
    return (selectedPattern?.item_list ?? []).flatMap(item => {
      const parentItem = { id: item.id, label: item.parentValue };
      const childItems = item.children.map(child => ({
        id: child.id,
        label: `${item.parentValue} > ${child.childValue}`,
      }));
      return childItems.length > 0 ? childItems : [parentItem];
    });
  };

  const resetNewMappingForm = (): void => {
    setNewCsvColumns([]);
    setNewRows([]);
    setNewPatternId('');
    setNewSearchItemId('');
    setNewSearchCsvColumn('');
    setNewColumnMappings([]);
  };

  const newPatternItems = buildPatternItems(newPatternId);

  // 新規辞書アップロード（CSV 本体作成 → 続けて1件目のマッピングを作成する2段階）
  const onNewUploadSubmit = async (file: File): Promise<void> => {
    setLoading(true);
    try {
      if (!user) throw new Error('ログインしてください。');
      if (!companyId) throw new Error('会社情報が取得できません。');

      const { csvColumns, rows } =
        newRows.length > 0 ? { csvColumns: newCsvColumns, rows: newRows } : await parseCsv(file);

      if (rows.length === 0) {
        throw new Error('データ行がありません。');
      }
      if (rows.length > DICTIONARY_MAX_ROWS) {
        throw new Error(`辞書は最大${DICTIONARY_MAX_ROWS}行までです。（現在: ${rows.length}行）`);
      }

      const selectedPattern = patterns.find(p => p.patternId === newPatternId);
      const patternName = selectedPattern?.pattern_name ?? '';
      const searchItemLabel = newPatternItems.find(i => i.id === newSearchItemId)?.label ?? '';

      // 1. 辞書本体（CSV データ）を作成する
      const dictionaryId = await createDictionary({
        companyId,
        fileName: file.name,
        rowCount: rows.length,
        csvColumns,
        rows,
        // 代理ログイン中は「事務局」名義で保存する（#3343）
        ...buildUploaderFields(user, isProxyMode, operatorId),
      });

      if (!dictionaryId) throw new Error('辞書の登録に失敗しました。');

      // 2. 1件目のマッピング（パターン設定）を作成する
      await createDictionaryMapping({
        dictionaryId,
        patternId: newPatternId,
        patternName,
        searchItemId: newSearchItemId,
        searchItemLabel,
        searchCsvColumn: newSearchCsvColumn,
        columnMappings: newColumnMappings,
      });

      setNewUploadModalOpen(false);
      reset({ images: [] });
      resetNewMappingForm();
      setExpandedDictionaryIds(prev => new Set(prev).add(dictionaryId));
      await fetchDictionaries();
    } catch (error: unknown) {
      const message = getUserErrorMessage(error, '予期せぬエラーが発生しました。');
      setErrorText(message);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // CSV差し替え（本体の行データ・列・件数のみ更新。マッピングは保持）
  const onReplaceSubmit = async (): Promise<void> => {
    setLoading(true);
    try {
      if (!user) throw new Error('ログインしてください。');
      if (!replaceTargetDictionary) return;

      const file = watch('images')?.[0];
      if (!file) return;
      if (file.name !== replaceTargetDictionary.file_name) {
        setFileError('選択したファイルの名前が一致しません。');
        setLoading(false);
        return;
      }

      const { csvColumns, rows } = await parseCsv(file);

      if (rows.length === 0) {
        throw new Error('データ行がありません。');
      }
      if (rows.length > DICTIONARY_MAX_ROWS) {
        throw new Error(`辞書は最大${DICTIONARY_MAX_ROWS}行までです。（現在: ${rows.length}行）`);
      }

      await updateDictionary({
        dictionaryId: replaceTargetDictionary.dictionary_id,
        rowCount: rows.length,
        csvColumns,
        rows,
        // 代理ログイン中は「事務局」名義で保存する（#3343）
        ...buildUploaderFields(user, isProxyMode, operatorId),
      });

      setReplaceModalOpen(false);
      setReplaceTargetDictionary(null);
      reset({ images: [] });
      await fetchDictionaries();
    } catch (error: unknown) {
      const message = getUserErrorMessage(error, '予期せぬエラーが発生しました。');
      setErrorText(message);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // マッピング追加/編集の保存
  const handleMappingSubmit = async (data: {
    patternId: string;
    patternName: string;
    searchItemId: string;
    searchItemLabel: string;
    searchCsvColumn: string;
    columnMappings: DictionaryColumnMapping[];
  }): Promise<void> => {
    if (!mappingTargetDictionary) return;
    try {
      if (editingMapping) {
        await updateDictionaryMapping({
          dictionaryId: mappingTargetDictionary.dictionary_id,
          mappingId: editingMapping.mapping_id,
          patternId: data.patternId,
          patternName: data.patternName,
          searchItemId: data.searchItemId,
          searchItemLabel: data.searchItemLabel,
          searchCsvColumn: data.searchCsvColumn,
          columnMappings: data.columnMappings,
        });
      } else {
        await createDictionaryMapping({
          dictionaryId: mappingTargetDictionary.dictionary_id,
          patternId: data.patternId,
          patternName: data.patternName,
          searchItemId: data.searchItemId,
          searchItemLabel: data.searchItemLabel,
          searchCsvColumn: data.searchCsvColumn,
          columnMappings: data.columnMappings,
        });
      }
      await fetchMappingsForDictionary(mappingTargetDictionary.dictionary_id);
      setMappingModalOpen(false);
      setMappingTargetDictionary(null);
      setEditingMapping(null);
    } catch {
      setErrorText('パターン設定の保存に失敗しました。');
      setSnackbarOpen(true);
    }
  };

  // 辞書の中身（rows）を CSV として出力する。辞書本体は Storage ではなく
  // Firestore に rows を埋め込んで持つため、rows + csv_columns からブラウザ側で
  // CSV を生成してダウンロードする（Excel 文字化け防止のため BOM を付与）。
  const handleDownloadDictionaryCsv = (dictionary: DictionaryType): void => {
    try {
      const csv = Papa.unparse({
        fields: dictionary.csv_columns,
        data: dictionary.rows as Record<string, string>[],
      });
      const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = dictionary.file_name || 'dictionary.csv';
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setErrorText('ファイルの出力に失敗しました。');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteDictionary = async (): Promise<void> => {
    if (!deleteTargetDictionary) return;
    try {
      setLoading(true);
      await deleteDictionary(deleteTargetDictionary.dictionary_id);
      setDeleteTargetDictionary(null);
      await fetchDictionaries();
    } catch {
      setErrorText('辞書の削除に失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMapping = async (): Promise<void> => {
    if (!deleteMappingTarget) return;
    const { dictionary, mapping } = deleteMappingTarget;
    try {
      setLoading(true);
      await deleteDictionaryMapping(dictionary.dictionary_id, mapping.mapping_id);
      await fetchMappingsForDictionary(dictionary.dictionary_id);
      setDeleteMappingTarget(null);
    } catch {
      setErrorText('パターン設定の削除に失敗しました。');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const openReplaceModal = (dictionary: DictionaryType): void => {
    setReplaceTargetDictionary(dictionary);
    reset({ images: [] });
    setFileError(null);
    setReplaceRowCount(0);
    setReplaceModalOpen(true);
  };

  const openMappingModal = (
    dictionary: DictionaryType,
    mapping: DictionaryMappingType | null,
  ): void => {
    setMappingTargetDictionary(dictionary);
    setEditingMapping(mapping);
    setMappingModalOpen(true);
  };

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          { '@media screen and (max-width:960px)': { paddingBottom: '84px' } },
        ]}
      >
        <Box sx={layoutStyles.header}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              minWidth: 0,
              marginRight: '18px',
              '@media screen and (max-width: 960px)': { marginRight: '0' },
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginRight: '18px',
                '@media screen and (max-width: 960px)': {
                  marginRight: '0',
                  fontSize: '12px',
                },
              }}
            >
              <Typography
                sx={{
                  ...boldXxl,
                  color: gray100,
                  '@media screen and (max-width:960px)': { ...spHeaderTitle },
                }}
              >
                辞書データ管理
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '13px',
                color: 'text.secondary',
                textAlign: 'left',
                '@media screen and (max-width:960px)': { display: 'none' },
              }}
            >
              {`辞書データ（CSV、最大${DICTIONARY_MAX_ROWS}件）を登録すると、読取結果を辞書の表記にあいまい一致で自動変換します。`}
            </Typography>
          </Box>
          <Box sx={{ ...styles.headerButtonWrapper, flexShrink: 0 }}>
            <Button
              startIcon={<UploadFileIcon />}
              sx={styles.uploadButton}
              onClick={(): void => {
                reset({ images: [] });
                setFileError(null);
                resetNewMappingForm();
                setNewUploadModalOpen(true);
              }}
            >
              <Typography sx={{ fontSize: '16px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                辞書登録
              </Typography>
            </Button>
          </Box>
        </Box>

        <Grid2 container flexDirection={'column'} gap={'16px'} width={'100%'}>
          <Box sx={styles.tableSection}>
            {loading ? (
              <CustomLoading loading={true} />
            ) : dictionaries.length > 0 ? (
              <Box sx={styles.tableWrapper}>
                <Table sx={styles.table}>
                  <TableHead>
                    <TableRow sx={styles.tableHeadRow}>
                      <TableCell sx={styles.tableHeadText} width={48}></TableCell>
                      <TableCell sx={styles.tableHeadText}>ファイル名</TableCell>
                      <TableCell sx={styles.tableHeadText}>パターン設定</TableCell>
                      <TableCell sx={styles.tableHeadText}>件数</TableCell>
                      <TableCell sx={styles.tableHeadText}>更新日</TableCell>
                      <TableCell sx={styles.tableHeadText}>作成者</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {dictionaries.map(data => {
                      const mappings = mappingsByDictionary[data.dictionary_id] ?? [];
                      const expanded = expandedDictionaryIds.has(data.dictionary_id);
                      const csvColumns = data.csv_columns ?? [];
                      return (
                        <React.Fragment key={data.dictionary_id}>
                          <TableRow sx={styles.tableDataRow}>
                            <TableCell sx={styles.tableDataText}>
                              <IconButton
                                size="small"
                                onClick={(): void => toggleExpand(data.dictionary_id)}
                                sx={styles.expandIconButton}
                              >
                                {expanded ? <ExpandMore /> : <ChevronRight />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={styles.tableDataText}>
                              <Tooltip title={data.file_name}>
                                <span style={styles.FileNameEllipsisText}>{data.file_name}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={styles.tableDataText}>
                              {mappings.length > 0 ? `${mappings.length}件` : '未設定'}
                            </TableCell>
                            <TableCell sx={styles.tableDataText}>{data.row_count + '件'}</TableCell>
                            <TableCell sx={styles.tableDataText}>
                              {data.updated_at
                                ? moment(data.updated_at.toDate()).format('YYYY/MM/DD HH:mm')
                                : '-'}
                            </TableCell>
                            <TableCell sx={styles.tableCellAvatar}>
                              <Avatar
                                src={
                                  data.is_proxy
                                    ? HiStoryIcon
                                    : data.uploaded_by_icon || AccountCircle
                                }
                                sx={styles.avatar}
                              />
                              <Typography>
                                {/* 代理アップロードは「事務局」と表示する（#3343） */}
                                <Tooltip
                                  title={getDisplayName(data.uploaded_by_name, data.is_proxy)}
                                >
                                  <span style={styles.CreatedByEllipsisText}>
                                    {getDisplayName(data.uploaded_by_name, data.is_proxy)}
                                  </span>
                                </Tooltip>
                              </Typography>
                            </TableCell>
                            <TableCell sx={styles.tableCellButton}>
                              <Box
                                sx={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}
                              >
                                <Button
                                  onClick={(): void => openReplaceModal(data)}
                                  startIcon={<UploadFileIcon sx={styles.icon18} />}
                                  sx={[secondaryButtonBackgroundColor, styles.buttonSmall]}
                                >
                                  CSV差替
                                </Button>
                                <Button
                                  onClick={(): void => handleDownloadDictionaryCsv(data)}
                                  startIcon={<FileDownloadOutlined sx={styles.icon18} />}
                                  sx={[tertiaryButtonBackgroundColor, styles.buttonSmall]}
                                >
                                  出力
                                </Button>
                                <Button
                                  onClick={(): void => setDeleteTargetDictionary(data)}
                                  startIcon={<DeleteOutline sx={styles.icon18} />}
                                  sx={[tertiaryButtonBackgroundColor, styles.buttonSmall]}
                                >
                                  削除
                                </Button>
                              </Box>
                            </TableCell>
                          </TableRow>

                          {expanded && (
                            <>
                              {mappings.map(mapping => (
                                <TableRow key={mapping.mapping_id} sx={styles.mappingRow}>
                                  <TableCell></TableCell>
                                  <TableCell sx={styles.mappingCell} colSpan={5}>
                                    <Box
                                      sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
                                    >
                                      <Typography sx={{ ...regularSm, fontWeight: 700 }}>
                                        {mapping.pattern_name || '未設定パターン'}
                                      </Typography>
                                      <Typography sx={{ ...regularSm, color: gray80 }}>
                                        検索：{mapping.search_item_label || '-'} /{' '}
                                        {mapping.search_csv_column || '-'}
                                      </Typography>
                                      {mapping.column_mappings &&
                                        mapping.column_mappings.length > 0 && (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              gap: '2px',
                                            }}
                                          >
                                            {mapping.column_mappings.map((cm, i) => (
                                              <Typography
                                                key={i}
                                                sx={{ ...regularSm, color: gray80 }}
                                              >
                                                マッピング：{cm.csv_column} →{' '}
                                                {cm.item_label || cm.item_id}
                                              </Typography>
                                            ))}
                                          </Box>
                                        )}
                                    </Box>
                                  </TableCell>
                                  <TableCell
                                    sx={[styles.tableCellButton, { padding: '8px 16px 8px 0' }]}
                                  >
                                    <Box
                                      sx={{
                                        display: 'inline-flex',
                                        gap: '8px',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Button
                                        onClick={(): void => openMappingModal(data, mapping)}
                                        sx={[secondaryButtonBackgroundColor, styles.buttonSmall]}
                                      >
                                        編集
                                      </Button>
                                      <Button
                                        onClick={(): void => {
                                          setDeleteMappingTarget({ dictionary: data, mapping });
                                        }}
                                        startIcon={<DeleteOutline sx={styles.icon18} />}
                                        sx={[tertiaryButtonBackgroundColor, styles.buttonSmall]}
                                      >
                                        削除
                                      </Button>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow sx={styles.mappingFooterRow}>
                                <TableCell></TableCell>
                                <TableCell colSpan={6} sx={styles.mappingCell}>
                                  <Button
                                    startIcon={<AddCircleOutlineIcon />}
                                    sx={[secondaryButtonBackgroundColor, styles.buttonSmall]}
                                    onClick={(): void => openMappingModal(data, null)}
                                    disabled={csvColumns.length === 0}
                                  >
                                    パターン設定を追加
                                  </Button>
                                  {csvColumns.length === 0 && (
                                    <Typography
                                      sx={{ ...regularSm, color: gray80, marginLeft: '8px' }}
                                      component="span"
                                    >
                                      CSVの列情報がありません。差し替えると再読込されます。
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            </>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <>
                <DescriptionIcon sx={styles.emptyIcon} />
                <Typography sx={styles.emptyTitle}>辞書データがありません</Typography>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={styles.emptyDescription}>
                    右上の「辞書登録」ボタンからCSVファイルをアップロードしてください。
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Grid2>
      </Box>

      <CustomSnackbar
        autoHideDuration={6000}
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
      />

      {/* 新規CSVアップロード＋1件目のマッピング設定ダイアログ */}
      <Dialog open={newUploadModalOpen} sx={styles.dialog}>
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitleText}>辞書データをアップロードする</Typography>
          <IconButton
            onClick={(): void => {
              setNewUploadModalOpen(false);
              resetNewMappingForm();
            }}
            sx={styles.iconButton}
          >
            <Close sx={styles.dialogCloseButton} />
          </IconButton>
        </Box>

        <Typography sx={{ ...regularSm, marginBottom: '16px' }}>
          CSVファイルをアップロードし、最初のパターン設定を登録します。
        </Typography>

        {watch('images') && watch('images')[0] && (
          <Box>
            <Chip
              label={watch('images')[0].name}
              onDelete={(): void => {
                handleFilesDeleted();
                resetNewMappingForm();
              }}
              sx={styles.uploadFileChip}
              variant="outlined"
            />
          </Box>
        )}

        <Controller
          control={control}
          name="images"
          render={({ field }): JSX.Element => (
            <FileUploader
              helperText={fileError || errors.images?.message}
              multiple={false}
              onFilesSelected={async (files: FileList | null): Promise<void> => {
                setFileError(null);
                if (files && files.length > 0) {
                  const file = files[0];
                  // 拡張子チェック（.csv のみ許可）
                  if (!/\.csv$/i.test(file.name)) {
                    field.onChange(null);
                    resetNewMappingForm();
                    setFileError('CSV ファイル (.csv) を選択してください。');
                    return;
                  }
                  field.onChange([file]);
                  const { csvColumns, rows } = await parseCsv(file);
                  setNewCsvColumns(csvColumns);
                  setNewRows(rows);
                  if (rows.length === 0) {
                    setFileError('データ行がありません。');
                  } else if (rows.length > DICTIONARY_MAX_ROWS) {
                    setFileError(
                      `辞書は最大${DICTIONARY_MAX_ROWS}行までです。（現在: ${rows.length}行）`,
                    );
                  }
                } else {
                  field.onChange(null);
                  resetNewMappingForm();
                }
              }}
            />
          )}
        />

        <Typography sx={{ ...regularSm, marginTop: '8px', marginBottom: '16px' }}>
          辞書は最大{DICTIONARY_MAX_ROWS}行までアップロード可能です。
        </Typography>

        {newCsvColumns.length > 0 && (
          <Box sx={{ marginBottom: '16px' }}>
            <Typography sx={{ ...boldMd, marginBottom: '4px', color: gray80 }}>
              検索対象項目（辞書項目）
            </Typography>
            <Typography
              sx={{ fontSize: '11px', color: gray80, lineHeight: 1.4, marginBottom: '4px' }}
            >
              辞書CSVのどのカラムであいまい検索するか
            </Typography>
            <CustomSelectField
              labelText=""
              list={newCsvColumns.map(col => ({ option: col, value: col }))}
              value={newSearchCsvColumn}
              placeholder="選択してください"
              onChange={(e): void => setNewSearchCsvColumn(String(e.target.value))}
              formMb="4px"
              height="40px"
              width="100%"
            />
            <Typography sx={{ fontSize: '11px', color: red50, lineHeight: 1.4 }}>
              ※ この項目を基準にあいまい検索を行います。
            </Typography>
          </Box>
        )}

        <CustomSelectField
          labelText="読取パターン"
          list={patterns.map(p => ({ option: p.pattern_name, value: p.patternId }))}
          value={newPatternId}
          placeholder="選択してください"
          onChange={(e): void => {
            setNewPatternId(String(e.target.value));
            setNewSearchItemId('');
            setNewColumnMappings([]);
          }}
          formMb="16px"
          height="40px"
          width="100%"
        />

        {newPatternId && newPatternItems.length > 0 && (
          <Box sx={{ marginBottom: '16px' }}>
            <Typography sx={{ ...boldMd, marginBottom: '4px', color: gray80 }}>
              変換対象項目（パターン項目）
            </Typography>
            <Typography
              sx={{ fontSize: '11px', color: gray80, lineHeight: 1.4, marginBottom: '4px' }}
            >
              読取結果のどの値で検索するか
            </Typography>
            <CustomSelectField
              labelText=""
              list={newPatternItems.map(i => {
                const usedByMaster = newMasterUsedItemIds.has(i.id);
                const usedByDictionary = newDictionaryUsedItemIds.has(i.id);
                const suffix = usedByMaster
                  ? '（マスタ設定済み）'
                  : usedByDictionary
                    ? '（辞書変換設定済み）'
                    : '';
                return {
                  option: `${i.label}${suffix}`,
                  value: i.id,
                  disabled: usedByMaster || usedByDictionary,
                };
              })}
              value={newSearchItemId}
              placeholder="選択してください"
              onChange={(e): void => setNewSearchItemId(String(e.target.value))}
              formMb="0px"
              height="40px"
              width="100%"
            />
          </Box>
        )}

        {newCsvColumns.length > 0 && newPatternId && (
          <Box sx={{ marginBottom: '8px' }}>
            <Typography sx={{ ...boldMd, marginBottom: '4px', color: gray80 }}>
              取得項目（辞書項目） → 格納先項目（パターン項目）
            </Typography>
            <Typography
              sx={{ fontSize: '11px', color: gray80, lineHeight: 1.4, marginBottom: '8px' }}
            >
              一致した行から取り出す辞書項目と、どのパターン項目に格納するかを設定します
            </Typography>
            {newColumnMappings.map((mapping, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CustomSelectField
                    labelText=""
                    list={newCsvColumns.map(col => ({ option: col, value: col }))}
                    value={mapping.csv_column}
                    placeholder="取得項目（辞書項目）"
                    onChange={(e): void => {
                      const updated = [...newColumnMappings];
                      updated[index] = { ...updated[index], csv_column: String(e.target.value) };
                      setNewColumnMappings(updated);
                    }}
                    formMb="0px"
                    height="40px"
                    width="100%"
                  />
                </Box>
                <Typography sx={{ ...regularSm, color: gray80 }}>→</Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CustomSelectField
                    labelText=""
                    list={newPatternItems
                      .filter(item => item.id !== newSearchItemId)
                      .map(i => ({ option: i.label, value: i.id }))}
                    value={mapping.item_id}
                    placeholder="格納先項目（パターン項目）"
                    onChange={(e): void => {
                      const updated = [...newColumnMappings];
                      const selectedItem = newPatternItems.find(i => i.id === e.target.value);
                      updated[index] = {
                        ...updated[index],
                        item_id: String(e.target.value),
                        item_label: selectedItem?.label || '',
                      };
                      setNewColumnMappings(updated);
                    }}
                    formMb="0px"
                    height="40px"
                    width="100%"
                  />
                </Box>
                <IconButton
                  onClick={(): void => {
                    setNewColumnMappings(newColumnMappings.filter((_, i) => i !== index));
                  }}
                  sx={{ padding: '4px' }}
                >
                  <RemoveCircleOutlineIcon sx={{ color: orange50, fontSize: '20px' }} />
                </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<AddCircleOutlineIcon />}
              onClick={(): void => {
                setNewColumnMappings([
                  ...newColumnMappings,
                  { csv_column: '', item_id: '', item_label: '' },
                ]);
              }}
              sx={{
                ...secondaryButtonBackgroundColor,
                height: '36px',
                marginTop: '4px',
                '& .MuiButton-icon': { marginRight: '4px' },
              }}
            >
              マッピング追加
            </Button>
          </Box>
        )}

        <Box sx={styles.uploadButtonWrapper}>
          <Button
            sx={styles.uploadCancelButton}
            onClick={(): void => {
              setNewUploadModalOpen(false);
              resetNewMappingForm();
            }}
          >
            <Typography sx={styles.uploadCancelButtonText}>キャンセル</Typography>
          </Button>
          <Button
            sx={styles.uploadSubmitButton}
            disabled={
              !watch('images')?.[0] ||
              !newPatternId ||
              !newSearchItemId ||
              !newSearchCsvColumn ||
              newRows.length === 0 ||
              newRows.length > DICTIONARY_MAX_ROWS ||
              newColumnMappings.some(m => !m.csv_column || !m.item_id)
            }
            onClick={async (): Promise<void> => {
              await handleSubmit(async data => onNewUploadSubmit(data.images[0]))();
            }}
          >
            <Typography sx={styles.uploadSubmitButtonText}>アップロード</Typography>
          </Button>
        </Box>
      </Dialog>

      {/* CSV差し替えダイアログ */}
      <Dialog open={replaceModalOpen} sx={styles.dialog}>
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitleText}>CSVを差し替える</Typography>
          <IconButton onClick={(): void => setReplaceModalOpen(false)} sx={styles.iconButton}>
            <Close sx={styles.dialogCloseButton} />
          </IconButton>
        </Box>

        <Typography sx={{ ...regularSm, marginBottom: '16px' }}>
          {replaceTargetDictionary?.file_name} を差し替えます。
          <br />
          同名のCSVファイルを選択してください。パターン設定は保持されます。
        </Typography>

        {watch('images') && watch('images')[0] && (
          <Box>
            <Chip
              label={watch('images')[0].name}
              onDelete={(): void => handleFilesDeleted()}
              sx={styles.uploadFileChip}
              variant="outlined"
            />
          </Box>
        )}

        <Controller
          control={control}
          name="images"
          render={({ field }): JSX.Element => (
            <FileUploader
              helperText={fileError || errors.images?.message}
              multiple={false}
              onFilesSelected={async (files: FileList | null): Promise<void> => {
                await handleReplaceFilesSelected(files, field.onChange);
              }}
            />
          )}
        />

        <Box sx={styles.uploadButtonWrapper}>
          <Button sx={styles.uploadCancelButton} onClick={(): void => setReplaceModalOpen(false)}>
            <Typography sx={styles.uploadCancelButtonText}>キャンセル</Typography>
          </Button>
          <Button
            sx={styles.uploadSubmitButton}
            disabled={
              !watch('images')?.[0] ||
              watch('images')?.[0]?.name !== replaceTargetDictionary?.file_name ||
              replaceRowCount === 0 ||
              replaceRowCount > DICTIONARY_MAX_ROWS
            }
            onClick={async (): Promise<void> => {
              await onReplaceSubmit();
            }}
          >
            <Typography sx={styles.uploadSubmitButtonText}>保存</Typography>
          </Button>
        </Box>
      </Dialog>

      {/* マッピング（パターン設定）追加/編集モーダル */}
      <DictionaryMappingFormModal
        open={mappingModalOpen}
        companyId={companyId}
        fileName={mappingTargetDictionary?.file_name ?? ''}
        csvColumns={mappingTargetDictionary?.csv_columns ?? []}
        patterns={patterns}
        initial={editingMapping}
        onClose={(): void => {
          setMappingModalOpen(false);
          setMappingTargetDictionary(null);
          setEditingMapping(null);
        }}
        onSubmit={handleMappingSubmit}
      />

      <DeleteModal
        open={!!deleteTargetDictionary}
        onClose={(): void => setDeleteTargetDictionary(null)}
        onDelete={(): void => {
          void handleDeleteDictionary();
        }}
        title="辞書"
        description={`「${deleteTargetDictionary?.file_name ?? ''}」を削除します。削除すると元に戻すことはできません。よろしいですか？`}
        loading={loading}
      />

      <DeleteModal
        open={!!deleteMappingTarget}
        onClose={(): void => setDeleteMappingTarget(null)}
        onDelete={(): void => {
          void handleDeleteMapping();
        }}
        title="パターン設定"
        description={`「${deleteMappingTarget?.mapping.pattern_name ?? ''}」のパターン設定を削除します。削除すると元に戻すことはできません。よろしいですか？`}
        loading={loading}
      />
    </Box>
  );
};

export default Dictionary;
